import { useEffect, useRef, useState } from 'react';

// The files of a paste: e.g. an image copied from a web page or a screenshot (which has no
// name of its own, so it is named by the time)
export const getPastedFiles = (data: DataTransfer | null): File[] => {
  if (data === null) {
    return [];
  }

  const files = Array.from(data.items ?? [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);

  return files.map((file) => {
    if (file.name && file.name !== 'image.png') {
      return file;
    }

    const extension = file.type.split('/')[1] ?? 'png';
    const time = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
    return new File([file], `liitetty-${time}.${extension}`, {
      type: file.type,
    });
  });
};

/**
 * A drop area takes files also with a paste (Ctrl+V): while the mouse is over the area or
 * the focus is in it. Text pastes elsewhere on the page are not touched. Returns the ref to
 * give to the area.
 */
const usePastedFiles = (
  onFiles: (files: File[]) => void,
  enabled = true
): ((element: HTMLElement | null) => void) => {
  // The area is followed as state, as it may be rendered only later (e.g. after loading)
  const [element, setElement] = useState<HTMLElement | null>(null);
  const hovered = useRef(false);
  const handler = useRef(onFiles);
  handler.current = onFiles;

  useEffect(() => {
    if (!enabled || element === null) {
      return undefined;
    }

    hovered.current = element.matches(':hover');

    const enter = () => {
      hovered.current = true;
    };
    const leave = () => {
      hovered.current = false;
    };
    const paste = (event: ClipboardEvent) => {
      const focusInArea =
        document.activeElement !== null &&
        element.contains(document.activeElement);

      if (!hovered.current && !focusInArea) {
        return;
      }

      const files = getPastedFiles(event.clipboardData);
      if (files.length === 0) {
        return;
      }

      event.preventDefault();
      handler.current(files);
    };

    element.addEventListener('mouseenter', enter);
    element.addEventListener('mouseleave', leave);
    document.addEventListener('paste', paste);

    return () => {
      element.removeEventListener('mouseenter', enter);
      element.removeEventListener('mouseleave', leave);
      document.removeEventListener('paste', paste);
    };
  }, [element, enabled]);

  return setElement;
};

export default usePastedFiles;
